<template>
  <!-- This example requires Tailwind CSS v2.0+ -->
  <div>
    <div class="mt-4">
      <el-input
        class="dashboard-session-search"
        placeholder="Search by email"
        prefix-icon="el-icon-search"
        v-model="search"
        id="dashboard-session-search"
      ></el-input>
      <div class="sm:float-right mt-4 sm:mt-0">
        <el-select class="dashboard-session-selection" v-model="sessionSelection" multiple placeholder="Filter" id="dashboard-session-filter">
          <el-option v-for="item in surveyResponses" :key="item.value" :label="item.label" :value="item.value" class="capitalize"> </el-option>
        </el-select>
      </div>
    </div>
    <div class="clear-both"></div>
    <div class="overflow-hidden sm:rounded-md rounded-lg">
      <ul class="">
        <li
          v-for="session in displayedSessions"
          :key="session._id"
          class="bg-white shadow-sm rounded-lg border-2 border-brand-sand my-3 hover:bg-brand-light-sand transition-colors"
        >
          <div class="px-4 py-6 sm:px-6 my-0 flex flex-grow justify-start">
            <div v-if="showSessionPlayback" class="w-16 flex-grow-0 flex-shrink-0">
              <font-awesome-icon
                v-if="session.recording"
                @click="view(session)"
                :icon="['fas', 'play-circle']"
                class="text-brand-medium-brown text-4xl mt-2 cursor-pointer hover:text-brand-blue transition-colors"
                id="session-playback-trigger"
              />
              <font-awesome-icon
                v-else
                @click="view(session)"
                :icon="['fas', 'video-slash']"
                class="text-brand-light-brown cursor-not-allowed text-3xl mt-2"
              />
            </div>
            <div class="flex-grow-1 w-full">
              <div class="flex items-center justify-between">
                <p v-if="session.customer && session.customer.email" class="text-sm font-semibold text-brand-brown truncate confidential-blurry-text">
                  <a class="hover:bg-gray-50 cursor-pointer" @click="copyToClipboard(session.customer.email)">
                    {{ session.customer.email }}
                  </a>
                  <font-awesome-icon
                    :icon="['fas', 'paste']"
                    class="hover:opacity-80 opacity-40 ml-1 cursor-pointer"
                    @click="copyToClipboard(session.customer.email)"
                  />
                </p>
                <p v-else class="text-sm font-semibold text-brand-brown truncate">
                  No customer information provided
                  <font-awesome-icon
                    v-if="false"
                    :icon="['fas', 'info-circle']"
                    class="hover:opacity-80 opacity-40 ml-1 cursor-pointer"
                    @click="copyToClipboard(session.customer.email)"
                  />
                </p>
                <div class="ml-2 flex-shrink-0 flex">
                  <p v-if="session.canceled" class="px-4 py-1 inline-flex text-xs leading-5 font-semibold rounded-md bg-red-100 text-red-700">
                    Cancelled
                  </p>
                  <p v-else-if="session.aborted" class="px-4 py-1 inline-flex text-xs leading-5 font-semibold rounded-md bg-gray-50 text-gray-700">
                    Aborted Flow
                  </p>
                </div>
              </div>
              <div class="mt-2 sm:flex sm:justify-between">
                <ul class="text-sm text-brand-medium-brown font-medium customer-details">
                  <li>
                    <time class="ml-1" :datetime="session.createdAt">{{ session.createdAt | timeAgoDate }}</time>
                  </li>
                  <li v-if="session.surveyChoiceId">
                    {{ surveyChoices[session.blueprintId][session.surveyChoiceId].value }}
                  </li>
                </ul>
              </div>
              <div v-if="session.feedback" class="mt-4 text-xs text-brand-medium-brown">
                <span class="font-semibold">Feedback:</span>
                <span class="ml-1">{{ session.feedback }}</span>
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div class="flex justify-center mt-4 dashboard-pagination">
        <el-pagination
          v-if="numPages > 1"
          layout="prev, pager, next, sizes"
          :page-size.sync="pageSize"
          :page-sizes="[5, 10, 20, 50, 100]"
          :current-page.sync="page"
          :total="filteredSessions.length"
          background
          id="dashboard-session-pagination"
        ></el-pagination>
      </div>
      <session-viewer
        class="dashboard-session-viewer"
        v-if="isVideoActive"
        :session="viewingSession"
        v-on:update:session="viewingSession = $event"
      ></session-viewer>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { OFFER_TYPE, DISCOUNT_TYPE } from '@/store/shared';
import { CopyToClipboard } from '@/mixins/copy-to-clipboard';
import SessionViewer from '@/components/SessionViewer.vue';

const sessionSelectOptions = [
  { label: 'Accepted Discount', value: OFFER_TYPE.DISCOUNT },
  { label: 'Accepted Pause', value: OFFER_TYPE.PAUSE },
  { label: 'Contacted Support', value: OFFER_TYPE.CONTACT },
  { label: 'Cancelled', value: 'canceled' },
];

export default {
  name: 'LiteSessionList',
  components: {
    SessionViewer,
  },
  mixins: [CopyToClipboard],
  props: {
    sessions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      OFFER_TYPE,
      DISCOUNT_TYPE,
      viewingSession: null,
      isVideoActive: false,
      search: '',
      sessionSelectOptions,
      sessionSelection: [],
      page: 1,
      pageSize: 6,
    };
  },
  computed: {
    ...mapGetters(['surveyChoices']),
    surveyResponses() {
      const responseSet = this.sessions.reduce((responses, session) => {
        if (session.surveyChoiceValue) {
          responses.add(session.surveyChoiceValue.toLowerCase());
        }
        return responses;
      }, new Set());
      return [...responseSet].map((r) => ({
        label: r,
        value: r,
      }));
    },
    showSessionPlayback() {
      return this.sessions.reduce((acc, s) => acc || s.recording, false);
    },
    filteredSessions() {
      const selectFilteredSessions = this.sessions.filter(this.matchesFilter);
      if (this.search) {
        const searchTerms = this.search.split(' ').map((t) => t.toLowerCase());
        // eslint-disable-next-line arrow-body-style
        return selectFilteredSessions.filter((p) => {
          return searchTerms.reduce((acc, term) => p.customer && p.customer.email.toLowerCase().includes(term) && acc, true);
        });
      }
      // no search
      return selectFilteredSessions;
    },
    displayedSessions() {
      const minIndex = (this.page - 1) * this.pageSize;
      const maxIndex = this.page * this.pageSize - 1;
      return this.filteredSessions.filter((session, index) => index >= minIndex && index <= maxIndex);
    },
    numPages() {
      return Math.ceil(this.filteredSessions.length / this.pageSize);
    },
  },
  methods: {
    view(session) {
      this.isVideoActive = true;
      this.viewingSession = session;
    },
    matchesFilter(session) {
      if (this.sessionSelection.length) {
        if (session.surveyChoiceValue) {
          return this.sessionSelection.includes(session.surveyChoiceValue.toLowerCase());
        }
        return false;
      }
      return true;
    },
  },
};
</script>
<style lang="scss" scoped>
.customer-details {
  @apply list-none;
  li {
    display: inline;
  }
  li + li::before {
    content: '  ·  ';
  }
}
</style>

<style lang="scss">
.dashboard-pagination {
  .el-pagination.is-background {
    button.btn-prev {
      @apply bg-brand-sand;
      &:hover {
        @apply text-gray-800;
      }
    }
    button.btn-next {
      @apply bg-brand-sand;
      &:hover {
        @apply text-gray-800;
      }
    }
    li.btn-quicknext {
      @apply bg-brand-sand;
      &:hover {
        @apply text-gray-800;
      }
    }
    li.btn-quickprev {
      @apply bg-brand-sand;
      &:hover {
        @apply text-gray-800;
      }
    }
    ul.el-pager {
      li.number {
        @apply bg-brand-sand;
        &:hover {
          @apply text-gray-800;
        }
        &.active {
          @apply bg-brand-medium-brown;
          &:hover {
            @apply text-white;
          }
        }
      }
    }
  }
  .el-input {
    @apply shadow-sm;
    @apply rounded-lg;
    .el-select__caret {
      @apply text-brand-blue;
    }
    .el-input__inner {
      @apply border-brand-gray-blue;
      @apply rounded-lg;
      border-width: 2px;
      &::placeholder {
        @apply text-brand-gray-blue;
      }
    }
  }
}

.dashboard-session-search {
  max-width: 300px;
  @apply shadow-sm;
  @apply rounded-md;
  .el-input__icon {
    @apply text-brand-gray-blue;
  }
  .el-input__inner {
    @apply border-brand-gray-blue;
    @apply rounded-lg;
    border-width: 2px;
    &::placeholder {
      @apply text-brand-gray-blue;
    }
  }
}

.dashboard-session-selection {
  max-width: 400px;
  @apply shadow-sm;
  @apply rounded-md;
  .el-select__caret {
    @apply text-brand-blue;
  }
  .el-input__inner {
    @apply border-brand-gray-blue;
    @apply rounded-lg;
    border-width: 2px;
    &::placeholder {
      @apply text-brand-gray-blue;
    }
  }
}

.dashboard-session-viewer {
  .playback {
    cursor: pointer;
  }
  .el-dialog {
    width: 66% !important;
    min-height: 600px;
  }
  .rr-player {
    height: 656px;
    width: 100% !important;
    float: none !important;
    margin: 0 auto !important;
  }
  .rr-player__frame {
    height: 576px;
    width: 100% !important;
    float: none !important;
    margin: 0 auto !important;
  }
  .el-loading-spinner .circular {
    height: 100px;
    width: 100px;
  }
  .el-loading-spinner {
    position: relative;
    right: -50%;
    top: 30%;
  }
  .el-dialog__headerbtn {
    top: 5px;
    right: 15px;
    font-size: 35px;
  }
}
</style>
